// TODO: survey.settings (builder settings) will be moved to core and handled here
// as changes to these create a new survey version that require republishing but shouldn't,
// i.e. add question behaviour doesn't change survey structure

import cloneDeep from 'lodash/cloneDeep';
import { DraftSetting } from '@/services/projectSettings';

export enum SettingType {
  Participation = 'participation', // persisted via project settings API, changes require republish
  Builder = 'builder'              // control functionality only, changes do not require a republish (to be migrated from survey.settings)
}

export enum ParticipationSetting {
  ProgressBar = 'display_progress_bar',
  Anonymize = 'anonymize',
  MultipleResponses = 'enable_multiple_responses',
  ShowBackButton = 'show_back_button',
  BackButtonType = 'back_button_type',
  NextButtonType = 'next_button_type',
  QuestionLabels = 'display_question_numbers_labels',
  QuestionNumbers = 'display_question_numbers',
  QuestionVisibility = 'question-visibility',
  PreSurveyQualityMeasures = 'pre_survey_quality_measures',
  PreviewCommentsEveryone = 'preview_comments_everyone',
  PreviewCommentsRestricted = 'preview_comments_restricted',
  PreviewCommentsEmails = 'preview_comments_emails'
}

//TODO: to be migrated from survey.settings
export enum BuilderSetting {
  AddQuestionBehavior = 'add_question_behavior',
  EnableAutoNodeCode = 'enable_auto_node_code',
  AddNewNodeToExistingPage = 'add_new_node_to_existing_page',
}

export enum QuestionVisibilityOptions {
  ExpandAllQuestions = 'expand-all-questions',
  ActiveQuestionsOnly = 'active-question-only'
}

export enum QuestionBehaviorOptions {
  CurrentPage = 'current-page',
  NewPage = 'new-page'
}

interface Setting {
  value: string | boolean | QuestionVisibilityOptions | QuestionBehaviorOptions;
  isChanged: boolean;
}

export type SettingValueType = {
  [ParticipationSetting.ProgressBar]: boolean;
  [ParticipationSetting.Anonymize]: boolean;
  [ParticipationSetting.MultipleResponses]: boolean;
  [ParticipationSetting.ShowBackButton]: boolean;
  [ParticipationSetting.BackButtonType]: string;
  [ParticipationSetting.NextButtonType]: string;
  [ParticipationSetting.QuestionLabels]: boolean;
  [ParticipationSetting.QuestionNumbers]: boolean;
  [ParticipationSetting.QuestionVisibility]: QuestionVisibilityOptions;
  [ParticipationSetting.PreSurveyQualityMeasures]: boolean;
  [ParticipationSetting.PreviewCommentsEveryone]: boolean;
  [ParticipationSetting.PreviewCommentsRestricted]: boolean;
  [ParticipationSetting.PreviewCommentsEmails]: string;
  [BuilderSetting.AddQuestionBehavior]: QuestionBehaviorOptions;
  [BuilderSetting.EnableAutoNodeCode]: boolean;
  [BuilderSetting.AddNewNodeToExistingPage]: boolean;
};

// helper types for filtering settings by type
type ParticipationSettingValues = Pick<SettingValueType, ParticipationSetting>;
type BuilderSettingValues = Pick<SettingValueType, BuilderSetting>;

// metadata to identify setting types
interface SettingMetadata<K extends keyof SettingValueType> {
  valueType: 'boolean' | 'string' | typeof QuestionVisibilityOptions | typeof QuestionBehaviorOptions;
  settingType: SettingType;
  defaultValue: SettingValueType[K];
}

type SettingMetadataMap = {
  [K in keyof SettingValueType]: SettingMetadata<K>;
};

export const SETTINGS_METADATA: SettingMetadataMap = {
  [ParticipationSetting.ProgressBar]: {
    valueType: 'boolean',
    settingType: SettingType.Participation,
    defaultValue: false
  },
  [ParticipationSetting.Anonymize]: {
    valueType: 'boolean',
    settingType: SettingType.Participation,
    defaultValue: false
  },
  [ParticipationSetting.MultipleResponses]: {
    valueType: 'boolean',
    settingType: SettingType.Participation,
    defaultValue: false
  },
  [ParticipationSetting.ShowBackButton]: {
    valueType: 'boolean',
    settingType: SettingType.Participation,
    defaultValue: false
  },
  [ParticipationSetting.BackButtonType]: {
    valueType: 'string',
    settingType: SettingType.Participation,
    defaultValue: 'text'
  },
  [ParticipationSetting.NextButtonType]: {
    valueType: 'string',
    settingType: SettingType.Participation,
    defaultValue: 'text'
  },
  [ParticipationSetting.QuestionLabels]: {
    valueType: 'boolean',
    settingType: SettingType.Participation,
    defaultValue: false
  },
  [ParticipationSetting.QuestionNumbers]: {
    valueType: 'boolean',
    settingType: SettingType.Participation,
    defaultValue: false
  },
  [ParticipationSetting.QuestionVisibility]: {
    valueType: QuestionVisibilityOptions,
    settingType: SettingType.Participation,
    defaultValue: QuestionVisibilityOptions.ActiveQuestionsOnly
  },
  [ParticipationSetting.PreSurveyQualityMeasures]: {
    valueType: 'boolean',
    settingType: SettingType.Participation,
    defaultValue: true
  },
  [ParticipationSetting.PreviewCommentsEveryone]: {
    valueType: 'boolean',
    settingType: SettingType.Participation,
    defaultValue: true
  },
  [ParticipationSetting.PreviewCommentsRestricted]: {
    valueType: 'boolean',
    settingType: SettingType.Participation,
    defaultValue: false
  },
  [ParticipationSetting.PreviewCommentsEmails]: {
    valueType: 'string',
    settingType: SettingType.Participation,
    defaultValue: ''
  },
  [BuilderSetting.AddQuestionBehavior]: {
    valueType: QuestionBehaviorOptions,
    settingType: SettingType.Builder,
    defaultValue: QuestionBehaviorOptions.CurrentPage
  },
  [BuilderSetting.EnableAutoNodeCode]: {
    valueType: 'boolean',
    settingType: SettingType.Builder,
    defaultValue: true
  },
  [BuilderSetting.AddNewNodeToExistingPage]: {
    valueType: 'boolean',
    settingType: SettingType.Builder,
    defaultValue: true
  }
};

//TODO: once builder settings migrated to core, remove filter on participation settings
export const SETTINGS_LIST = Object.keys(SETTINGS_METADATA).filter(
  key => SETTINGS_METADATA[key as keyof SettingValueType].settingType === SettingType.Participation
);

export const PARTICIPATION_SETTINGS_LIST = Object.keys(SETTINGS_METADATA).filter(
  key => SETTINGS_METADATA[key as keyof SettingValueType].settingType === SettingType.Participation
);

export const BUILDER_SETTINGS_LIST = Object.keys(SETTINGS_METADATA).filter(
  key => SETTINGS_METADATA[key as keyof SettingValueType].settingType === SettingType.Builder
);

export const SETTINGS_DEFAULT_VALUE = Object.keys(SETTINGS_METADATA).reduce((acc, key) => {
  const typedKey = key as keyof SettingValueType;
  (acc as any)[typedKey] = SETTINGS_METADATA[typedKey].defaultValue;
  return acc;
}, {} as SettingValueType);

export class Settings {
  private data: Map<string, Setting>;

  constructor(data?: Map<string, Setting>) {
    // initialize with defaults
    this.data = new Map();
    Object.entries(SETTINGS_DEFAULT_VALUE).forEach(([key, value]) => {
      this.data.set(key, {
        value: value,
        isChanged: false
      });
    });

    // override with provided data
    if (data) {
      data.forEach((setting, key) => {
        this.data.set(key, setting);
      });
    }
  }

  getSetting<K extends keyof SettingValueType>(key: K): SettingValueType[K] {
    const setting = this.data.get(key as string);
    return setting?.value as SettingValueType[K];
  }

  setSetting<K extends keyof SettingValueType>(key: K, value: SettingValueType[K], isChanged?: boolean) {
    const existing = this.data.get(key as string);
    this.data.set(key as string, {
      value: value,
      isChanged: isChanged !== undefined ? isChanged : (existing?.isChanged || false)
    });
  }

  hasUnpublishedChanges(): boolean {
    // only care about participation settings; builder settings do not require republish
    return Array.from(this.data.entries()).some(([key, setting]) =>
      this.isParticipationSetting(key) && setting.isChanged === true
    );
  }

  clearIsChangedFlags(): void {
    this.data.forEach((setting, key) => {
      setting.isChanged = false;
    });
  }

  clone(): Settings {
    return cloneDeep(this);
  }

  isParticipationSetting(key: string): boolean {
    const metadata = SETTINGS_METADATA[key as keyof SettingValueType];
    return metadata?.settingType === SettingType.Participation;
  }

  isBuilderSetting(key: string): boolean {
    const metadata = SETTINGS_METADATA[key as keyof SettingValueType];
    return metadata?.settingType === SettingType.Builder;
  }

  getParticipationSettings(): ParticipationSettingValues {
    const projectSettings = {} as ParticipationSettingValues;
    this.data.forEach((setting, key) => {
      if (this.isParticipationSetting(key)) {
        Object.assign(projectSettings, { [key]: setting.value });
      }
    });
    return projectSettings;
  }

  getBuilderSettings(): BuilderSettingValues {
    const builderSettings = {} as BuilderSettingValues;
    this.data.forEach((setting, key) => {
      if (this.isBuilderSetting(key)) {
        Object.assign(builderSettings, { [key]: setting.value });
      }
    });
    return builderSettings;
  }

  toApiFormat(): Record<string, string> {
    const apiFormat: Record<string, string> = {};

    // convert all participation settings values to strings for API
    this.data.forEach((setting, key) => {
      // TODO: once builder settings migrated to core, remove if condition on participation settings
      if (this.isParticipationSetting(key)) {
        apiFormat[key] = String(setting.value);
      }
    });

    return apiFormat;
  }

  static fromApiResponse(settingsList: DraftSetting[]): Settings {
    const data = new Map<string, Setting>();

    settingsList.forEach(apiSetting => {
      const metadata = SETTINGS_METADATA[apiSetting.name as keyof SettingValueType];
      if (metadata) {
        let parsedValue: any = apiSetting.value;

        // parse boolean values based on expected type
        if (metadata.valueType === 'boolean') {
          parsedValue = apiSetting.value === 'true';
        }

        data.set(apiSetting.name, {
          value: parsedValue,
          isChanged: apiSetting.isChanged || false
        });
      }
    });

    return new Settings(data);
  }
}
